import * as logger from '../logger'

class ResponseError extends Error {
  constructor(public statusCode, message: string, public error: any) {
    super(message)
  }
}

export function checkHttpStatus(response, allow4xx = false, skipUnauthorizedCheck = false) {
  if (response.status >= 200 && response.status < 300) {
    return response
  }

  if (!skipUnauthorizedCheck && response.status === 401) {
    removeAuthTokens()
    return window.location.reload()
  }

  if (allow4xx && response.status >= 400 && response.status < 500) {
    return response
  }

  return new Promise((res, rej) => {
    response.json().then((json) => {
      rej(new ResponseError(response.status, response.statusText, json.error))
    })
  })
}

export function getAuthTokens() {
  const token = localStorage.getItem('token') || ''
  return token
}

/**
 * Save auth token in both old format and new gnowbe-frontend format
 * This allows seamless transition from old web to new web without re-login
 */
export function saveAuthTokens(token: string, tokenData?: {
  token_type?: string;
  expires_at?: number;
  expires_in?: number;
  userId?: string;
}) {
  // Save in old format (backward compatibility)
  localStorage.setItem('token', token)
  
  // Save in new gnowbe-frontend format (for cross-app auth)
  try {
    // Decode JWT to extract userId if not provided
    let userId = tokenData?.userId
    if (!userId && token) {
      try {
        const decoded: any = JSON.parse(atob(token.split('.')[1]))
        userId = decoded.uid || decoded.sub
      } catch (e) {
        console.warn('Could not decode JWT for userId:', e)
      }
    }

    // Save with mmkv.default\ namespace to match gnowbe-frontend on web
    localStorage.setItem('mmkv.default\\gnowbe_access_token', token)
    
    if (tokenData?.token_type) {
      localStorage.setItem('mmkv.default\\gnowbe_token_type', tokenData.token_type)
    } else {
      localStorage.setItem('mmkv.default\\gnowbe_token_type', 'Bearer')
    }
    
    if (tokenData?.expires_at) {
      localStorage.setItem('mmkv.default\\gnowbe_expires_at', tokenData.expires_at.toString())
    }
    
    if (tokenData?.expires_in) {
      localStorage.setItem('mmkv.default\\gnowbe_expires_in', tokenData.expires_in.toString())
    }
    
    if (userId) {
      localStorage.setItem('mmkv.default\\gnowbe_user_id', userId)
    }
  } catch (error) {
    console.error('Failed to save token in gnowbe-frontend format:', error)
    // Don't throw - old format is already saved
  }
}

export function removeAuthTokens() {
  // Remove old format
  localStorage.removeItem('token')
  
  // Remove new gnowbe-frontend format
  localStorage.removeItem('mmkv.default\\gnowbe_access_token')
  localStorage.removeItem('mmkv.default\\gnowbe_token_type')
  localStorage.removeItem('mmkv.default\\gnowbe_expires_at')
  localStorage.removeItem('mmkv.default\\gnowbe_expires_in')
  localStorage.removeItem('mmkv.default\\gnowbe_user_id')
}

interface SubscriptionData {
  data: any;
  fireRef: any;
  valueListener?;
  childAddedListener?;
  childChangedListener?;
  childRemovedListener?;
  counter: number;
}

// Ideally this would be in Redux store but it is here now :)
let subscriptions: {[key: string]: SubscriptionData} = {}

export function checkAndIncSubscription(subKey: string): boolean {
  if (subscriptions[subKey]) {
    subscriptions[subKey].counter = (subscriptions[subKey].counter || 1) + 1
    return true
  }
  return false
}

export function addSubscription(subKey: string, data: SubscriptionData) {
  subscriptions[subKey] = data
}

export function endSubscription(subKey: string, force?: boolean): SubscriptionData|null {
  const data = subscriptions[subKey]
  if (!data) return null
  if (data.counter > 1 && !force) {
    data.counter = data.counter - 1
    return null
  }
  data.valueListener && data.fireRef.off('value', data.valueListener)
  data.childAddedListener && data.fireRef.off('child_added', data.childAddedListener)
  data.childChangedListener && data.fireRef.off('child_changed', data.childChangedListener)
  data.childRemovedListener && data.fireRef.off('child_removed', data.childRemovedListener)
  delete subscriptions[subKey]
  return data
}

export function clearSubscriptionsData() {
  subscriptions = {}
}
